import { useState, useCallback, useContext } from 'react';
import { addDays, format, isSameDay } from 'date-fns';
import csLocale from 'date-fns/locale/cs';
import { Container, Box, Typography, Chip, Snackbar, Alert } from '@mui/material';
import { Pending } from '@mui/icons-material';
import AuthContext from '../contexts/AuthContext';
import { useAxios } from '../hooks/useAxios';
import {
  useEmployeeCalendarData,
  getMondayWeekStart,
  formatDayKey,
} from '../hooks/useEmployeeCalendarData';
import {
  getTodayStart,
  hasPendingApplication,
  isAssignedAvailability,
} from '../utils/employeeCalendarUtils';
import { getIsoDate } from '../utils/dateHandler';
import MobileWeekChrome, { jumpWeekToDay } from './MobileWeekChrome';
import { useEmployeeShiftDialogs } from './EmployeeShiftDialogs';

const MobileAvailableShifts = () => {
  const todayStart = getTodayStart();
  const { employeeId } = useContext(AuthContext);
  const [weekStart, setWeekStart] = useState(() => getMondayWeekStart(todayStart));
  const [selectedDay, setSelectedDay] = useState(todayStart);

  const { availabilities, offeredAllocations, openAllocations, refresh, weekDays, visibleMonth } =
    useEmployeeCalendarData(weekStart);

  const { openAllocationDialog, openAvailabilityDialog, dialogs } =
    useEmployeeShiftDialogs(refresh);
  const { requestStatus, clearRequestStatus } = useAxios();

  const handleJumpToDay = useCallback((day) => {
    jumpWeekToDay(day, setWeekStart, setSelectedDay);
  }, []);

  const handleSelectDay = useCallback((day) => {
    setSelectedDay(day);
  }, []);

  const handlePreviousWeek = () => setWeekStart((start) => addDays(start, -7));
  const handleNextWeek = () => setWeekStart((start) => addDays(start, 7));

  const weekHasItems = weekDays.some((day) => {
    const key = formatDayKey(day);
    const bookedCount = (availabilities[key] || []).filter((a) => isAssignedAvailability(a)).length;
    const availableCount =
      (offeredAllocations[key]?.length || 0) + (openAllocations[key]?.length || 0);
    return bookedCount > 0 || availableCount > 0;
  });

  return (
    <>
      <Container maxWidth="sm" sx={{ pb: 1.5 }}>
        <MobileWeekChrome
          weekStart={weekStart}
          selectedDay={selectedDay}
          onSelectDay={handleSelectDay}
          onPreviousWeek={handlePreviousWeek}
          onNextWeek={handleNextWeek}
          visibleMonth={visibleMonth}
          availabilities={availabilities}
          offeredAllocations={offeredAllocations}
          openAllocations={openAllocations}
          onJumpToDay={handleJumpToDay}
        />

        {!weekHasItems && (
          <Typography className="glass-day-empty" sx={{ mb: 1 }}>
            Žádné směny tento týden.
          </Typography>
        )}

        <Box className="glass-week-day-list">
          {weekDays.map((day) => {
            const dayKey = formatDayKey(day);
            const dayAvailabilities = availabilities[dayKey] || [];
            const booked = dayAvailabilities.filter((a) => isAssignedAvailability(a));
            const offered = offeredAllocations[dayKey] || [];
            const open = openAllocations[dayKey] || [];
            const items = [
              ...booked.map((a) => ({
                type: 'booked',
                data: a,
                key: `booked-${a._id}`,
                sortTime: new Date(a.startTime),
              })),
              ...offered.map((a) => ({
                type: 'offered',
                data: a,
                key: `offered-${a._id}`,
                sortTime: new Date(a.startTime),
              })),
              ...open.map((a) => ({
                type: 'open',
                data: a,
                key: `open-${a._id}`,
                sortTime: new Date(a.startTime),
              })),
            ].sort((a, b) => a.sortTime - b.sortTime);

            if (items.length === 0) return null;

            const isToday = isSameDay(day, todayStart);
            const isSelected = isSameDay(day, selectedDay);

            return (
              <Box
                key={dayKey}
                className="glass-day-section"
                data-today={isToday ? 'true' : 'false'}
                data-selected={isSelected ? 'true' : 'false'}
              >
                <Typography className="glass-day-section-title" sx={{ mb: 0.75 }}>
                  {format(day, 'EEEE d.M.', { locale: csLocale })}
                </Typography>

                {items.map((item) => {
                  if (item.type === 'booked') {
                    const availability = item.data;
                    const shift = availability.shiftAllocationId;

                    return (
                      <button
                        key={item.key}
                        type="button"
                        className="glass-list-card glass-shift-fill glass-list-card-button"
                        onClick={() => openAvailabilityDialog(day, availability)}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                          {availability.isOfferRequested && (
                            <Box sx={{ mb: 0.15 }}>
                              <Chip size="small" label="Nabízená" className="glass-chip-offered" />
                            </Box>
                          )}
                          <Typography className="glass-list-card-time">
                            {format(getIsoDate(availability.startTime), 'HH:mm')} –{' '}
                            {format(getIsoDate(availability.endTime), 'HH:mm')}
                          </Typography>
                          {shift && (
                            <Typography className="glass-list-card-title">
                              {shift.name}
                              {shift.outlet?.name && ` · ${shift.outlet.name}`}
                            </Typography>
                          )}
                          {availability.isCancellationRequested && (
                            <Typography className="glass-list-card-meta">
                              Žádost o zrušení
                            </Typography>
                          )}
                        </Box>
                      </button>
                    );
                  }

                  const allocation = item.data;
                  const pending = hasPendingApplication(allocation, employeeId);
                  const borderClass =
                    item.type === 'offered' ? 'glass-offered-border' : 'glass-open-border';
                  const typeLabel = item.type === 'offered' ? 'Nabízená' : 'Volná';

                  return (
                    <button
                      key={item.key}
                      type="button"
                      className={`glass-list-card ${borderClass} glass-list-card-button`}
                      onClick={() => openAllocationDialog(allocation, item.type)}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 0.75,
                            mb: 0.25,
                          }}
                        >
                          <Chip
                            size="small"
                            label={typeLabel}
                            className={
                              item.type === 'offered' ? 'glass-chip-offered' : 'glass-chip-open'
                            }
                          />
                          {pending && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.35,
                                flexShrink: 0,
                              }}
                            >
                              <Pending sx={{ fontSize: 16, color: '#ff9800' }} />
                              <Typography variant="caption" sx={{ color: 'var(--ink-3)' }}>
                                Čeká na schválení
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <Typography className="glass-list-card-time">
                          {format(getIsoDate(allocation.startTime), 'HH:mm')} –{' '}
                          {format(getIsoDate(allocation.endTime), 'HH:mm')}
                        </Typography>
                        <Typography className="glass-list-card-title">
                          {allocation.name}
                          {allocation.outlet?.name && ` · ${allocation.outlet.name}`}
                        </Typography>
                        {item.type === 'offered' && allocation.employee?.fullNameReversed && (
                          <Typography className="glass-list-card-meta">
                            Nabídl: {allocation.employee.fullNameReversed}
                          </Typography>
                        )}
                      </Box>
                    </button>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      </Container>

      {dialogs}

      <Snackbar
        open={!!requestStatus}
        autoHideDuration={requestStatus?.duration || 6000}
        onClose={clearRequestStatus}
      >
        <Alert
          severity={requestStatus?.severity || 'info'}
          onClose={clearRequestStatus}
          className="glass-alert-snackbar"
        >
          {requestStatus?.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MobileAvailableShifts;
